// SteelOps — Public bid review portal
//
// Purpose:
//   Lets a general contractor / structural engineer view, approve, or decline
//   a bid via a tokenized public link — no sign-in required. Mirrors
//   EstimatePortal.jsx for brand consistency.
//
// When reached:
//   App.jsx route /bid/:token (matched before AuthGate). Token comes from the
//   bids.token column generated server-side when the shop sent the bid.
//
// Who can use it:
//   Anyone with the link. Read/write goes through edge functions deployed
//   with --no-verify-jwt:
//     - bid-public-get  -> returns { bid, line_items, customer, company }
//     - bid-respond     -> records approve/decline + signature
//
// Anything not on this screen (PDF download, GC comment thread, change-order
// negotiation) lives on the authenticated owner-side UI.

import { useEffect, useState } from 'react';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const ANON_KEY     = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

async function callEdge(name, body) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey':       ANON_KEY,
    },
    body: JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch { /* tolerate non-JSON errors */ }
  return { ok: res.ok, status: res.status, data };
}

function dollars(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
}

// Bid line item total: (labor + material) * qty, then markup applied.
function lineTotal(li) {
  const qty      = Number(li.qty || 0);
  const labor    = Number(li.labor_hrs_unit || 0);
  const material = Number(li.material_cost_unit || 0);
  const markup   = Number(li.markup_pct || 0);
  const unit     = labor + material;
  return qty * unit * (1 + markup / 100);
}

function statusBadge(status) {
  const map = {
    draft:    { bg: 'var(--s3)',    color: 'var(--t2)',  label: 'Draft' },
    sent:     { bg: 'var(--teal-t)', color: 'var(--teal)', label: 'Awaiting response' },
    approved: { bg: 'var(--teal-t)', color: 'var(--teal)', label: 'Approved' },
    declined: { bg: 'var(--rose-t)', color: 'var(--rose)', label: 'Declined' },
    expired:  { bg: 'var(--s3)',     color: 'var(--t2)',  label: 'Expired' },
  };
  const s = map[status] || map.draft;
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 99,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '.06em',
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.color}33`,
    }}>{s.label}</span>
  );
}

export default function BidPortal({ token }) {
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [data,       setData]       = useState(null);
  const [signature,  setSignature]  = useState('');
  const [notes,      setNotes]      = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(null); // 'approved' | 'declined'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await callEdge('bid-public-get', { token });
      if (cancelled) return;
      if (!res.ok) {
        setError(res.data?.error || `Error ${res.status}`);
      } else {
        setData(res.data);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  async function respond(decision) {
    if (decision === 'approved' && !signature.trim()) {
      setError('Please type your name to sign and approve.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const res = await callEdge('bid-respond', {
      token,
      decision,
      signature: decision === 'approved' ? signature.trim() : null,
      notes:     notes.trim() || null,
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.data?.error || `Error ${res.status}`);
      return;
    }
    setSubmitted(decision);
  }

  if (loading) {
    return (
      <div style={portalShell}>
        <div style={portalCard}>
          <div style={{ color: 'var(--t3)', textAlign: 'center', padding: 40 }}>Loading…</div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={portalShell}>
        <div style={portalCard}>
          <h2 style={{ marginTop: 0 }}>Bid unavailable</h2>
          <p style={{ color: 'var(--t2)' }}>{error}</p>
          <p style={{ color: 'var(--t3)', fontSize: 13 }}>
            Check your link — bids expire after their valid-until date. If you
            think this is a mistake, reply to the email you received.
          </p>
        </div>
      </div>
    );
  }

  const { bid, line_items: lineItems = [], customer, company } = data;
  const isExpired = bid.status === 'expired'
    || (bid.valid_until && new Date(bid.valid_until) < new Date(new Date().toDateString()));
  const canRespond = bid.status === 'sent' && !isExpired;

  // Totals row computed client-side from line items.
  const totals = lineItems.reduce((acc, li) => acc + lineTotal(li), 0);

  const brand = company?.brand_color || 'var(--teal)';

  return (
    <div style={portalShell}>
      <div style={portalCard}>
        {/* Brand color stripe */}
        <div style={{ height: 4, background: brand, margin: '-28px -28px 18px', borderRadius: '14px 14px 0 0' }} />

        {/* Company header */}
        <header style={{ borderBottom: '1px solid var(--b2)', paddingBottom: 14, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 14 }}>
          {company?.logo_url
            ? <img src={company.logo_url} alt="" style={{ height: 48, width: 48, objectFit: 'contain', borderRadius: 8, background: 'var(--s2)' }} />
            : null}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Bid from
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--t1)' }}>
              {company?.name || 'Your fabrication shop'}
            </div>
            {customer?.name && (
              <div style={{ marginTop: 4, fontSize: 13, color: 'var(--t2)' }}>
                Prepared for <strong>{customer.name}</strong>
              </div>
            )}
          </div>
          <div>{statusBadge(isExpired && bid.status === 'sent' ? 'expired' : bid.status)}</div>
        </header>

        {/* Bid metadata box */}
        <div style={metaBox}>
          <div>
            <div style={metaLabel}>Bid number</div>
            <div style={metaValue}>{bid.bid_number || `#${bid.id}`}</div>
          </div>
          <div>
            <div style={metaLabel}>Project</div>
            <div style={metaValue}>{bid.project_name || '—'}</div>
          </div>
          <div>
            <div style={metaLabel}>Bid date</div>
            <div style={metaValue}>{fmtDate(bid.bid_date)}</div>
          </div>
          <div>
            <div style={metaLabel}>Valid until</div>
            <div style={metaValue}>{fmtDate(bid.valid_until)}</div>
          </div>
        </div>

        {/* Line items */}
        <div style={{ marginBottom: 22 }}>
          {lineItems.length === 0 ? (
            <div style={{ color: 'var(--t3)' }}>No line items.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--b2)' }}>
                    <th style={th}>Assembly</th>
                    <th style={th}>Description</th>
                    <th style={{ ...th, textAlign: 'right', width: 60 }}>Qty</th>
                    <th style={{ ...th, textAlign: 'right', width: 60 }}>Unit</th>
                    <th style={{ ...th, textAlign: 'right', width: 90 }}>Labor</th>
                    <th style={{ ...th, textAlign: 'right', width: 90 }}>Material</th>
                    <th style={{ ...th, textAlign: 'right', width: 70 }}>Markup</th>
                    <th style={{ ...th, textAlign: 'right', width: 110 }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, i) => (
                    <tr key={li.id ?? i} style={{ borderBottom: '1px solid var(--b1)' }}>
                      <td style={td}>{li.assembly_type || li.sub_assembly || '—'}</td>
                      <td style={td}>{li.description || '—'}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{li.qty ?? '—'}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{li.unit || '—'}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{dollars(li.labor_hrs_unit)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{dollars(li.material_cost_unit)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{Number(li.markup_pct || 0)}%</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{dollars(lineTotal(li))}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={7} style={{ ...td, textAlign: 'right', fontWeight: 700, paddingTop: 14 }}>Total</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, fontSize: 18, paddingTop: 14 }}>
                      {dollars(totals || (bid.total_cents != null ? bid.total_cents / 100 : 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {bid.notes && (
            <div style={{ marginTop: 18, padding: 12, background: 'var(--s2)', borderRadius: 8, fontSize: 13, color: 'var(--t2)', whiteSpace: 'pre-wrap' }}>
              {bid.notes}
            </div>
          )}

          <div style={{ marginTop: 14, fontSize: 12, color: 'var(--t3)' }}>
            This bid is valid until <strong>{fmtDate(bid.valid_until)}</strong>.
          </div>
        </div>

        {/* Terminal states */}
        {submitted === 'approved' && (
          <div style={successBox}>
            <strong>Bid approved.</strong> Thanks, {signature || customer?.name || 'there'} — {company?.name || 'the shop'} will be in touch.
          </div>
        )}
        {submitted === 'declined' && (
          <div style={successBox}>
            <strong>Bid declined.</strong> We've let {company?.name || 'the shop'} know.
          </div>
        )}

        {!submitted && bid.status === 'approved' && (
          <div style={successBox}>
            This bid was <strong>approved</strong>{bid.approved_at ? ` on ${fmtDate(bid.approved_at)}` : ''}. No further action is needed.
          </div>
        )}
        {!submitted && bid.status === 'declined' && (
          <div style={{ ...successBox, background: 'var(--rose-t)', border: '1px solid var(--rose)' }}>
            This bid was <strong>declined</strong>{bid.declined_at ? ` on ${fmtDate(bid.declined_at)}` : ''}.
          </div>
        )}
        {!submitted && isExpired && bid.status !== 'approved' && bid.status !== 'declined' && (
          <div style={{ ...successBox, background: 'var(--s2)' }}>
            <strong>This bid has expired.</strong>{' '}
            {company?.email
              ? <a href={`mailto:${company.email}?subject=Request new bid: ${encodeURIComponent(bid.bid_number || '')}`} style={{ color: brand }}>Request a new bid →</a>
              : 'Reply to the email you received to request a fresh quote.'}
          </div>
        )}

        {/* Response form */}
        {!submitted && canRespond && (
          <div style={{ borderTop: '1px solid var(--b2)', paddingTop: 18 }}>
            <label style={fieldLabel}>Type your full name to sign</label>
            <input
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder={customer?.name || 'Your name'}
              style={input}
            />

            <label style={{ ...fieldLabel, marginTop: 14 }}>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything we should know about scope, schedule, etc."
              rows={3}
              style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
            />

            {error && <div style={errorBox}>{error}</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button
                style={btnApprove}
                disabled={submitting}
                onClick={() => respond('approved')}
              >
                {submitting ? 'Submitting…' : 'Approve bid'}
              </button>
              <button
                style={btnDecline}
                disabled={submitting}
                onClick={() => respond('declined')}
              >
                Decline
              </button>
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--t3)' }}>
              Expires {fmtDate(bid.valid_until)}.
            </div>
          </div>
        )}

        <footer style={{ marginTop: 28, fontSize: 11, color: 'var(--t3)', textAlign: 'center' }}>
          Powered by SteelOps
        </footer>
      </div>
    </div>
  );
}

const portalShell = {
  minHeight:       '100vh',
  display:         'flex',
  alignItems:      'flex-start',
  justifyContent:  'center',
  padding:         '32px 16px',
  background:      'var(--bg)',
};
const portalCard = {
  width:           '100%',
  maxWidth:        820,
  background:      'var(--s1)',
  borderRadius:    14,
  border:          '1px solid var(--b1)',
  padding:         '28px 28px 22px',
  boxShadow:       '0 12px 40px rgba(0,0,0,.18)',
  color:           'var(--t1)',
  overflow:        'hidden',
};
const metaBox = {
  display:             'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap:                 12,
  padding:             '14px 16px',
  background:          'var(--s2)',
  borderRadius:        10,
  marginBottom:        18,
};
const metaLabel = {
  fontSize:       10,
  fontWeight:     700,
  color:          'var(--t3)',
  textTransform:  'uppercase',
  letterSpacing:  '.06em',
  marginBottom:   3,
};
const metaValue = {
  fontSize:   14,
  fontWeight: 600,
  color:      'var(--t1)',
};
const th = { textAlign: 'left', padding: '8px 6px', fontSize: 11, color: 'var(--t3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' };
const td = { padding: '10px 6px', fontSize: 13, color: 'var(--t1)' };
const fieldLabel = {
  display:        'block',
  fontSize:       12,
  fontWeight:     600,
  color:          'var(--t2)',
  marginBottom:   6,
  textTransform:  'uppercase',
  letterSpacing:  '.05em',
};
const input = {
  width:        '100%',
  padding:      '10px 12px',
  fontSize:     16,
  borderRadius: 8,
  border:       '1px solid var(--b2)',
  background:   'var(--s2)',
  color:        'var(--t1)',
  boxSizing:    'border-box',
};
const btnApprove = {
  flex:         1,
  padding:      '12px 16px',
  fontSize:     15,
  fontWeight:   600,
  borderRadius: 9,
  border:       'none',
  cursor:       'pointer',
  background:   'var(--teal)',
  color:        '#001216',
};
const btnDecline = {
  padding:      '12px 16px',
  fontSize:     15,
  fontWeight:   600,
  borderRadius: 9,
  border:       '1px solid var(--b2)',
  cursor:       'pointer',
  background:   'transparent',
  color:        'var(--t2)',
};
const successBox = {
  marginTop:    18,
  padding:      14,
  background:   'var(--teal-t)',
  border:       '1px solid var(--teal)',
  borderRadius: 9,
  color:        'var(--t1)',
  fontSize:     14,
};
const errorBox = {
  marginTop:    10,
  padding:      10,
  background:   'var(--rose-t)',
  border:       '1px solid var(--rose)',
  borderRadius: 8,
  color:        'var(--t1)',
  fontSize:     13,
};
